<template>
  <div id="app" :class="appClassNames">
    <template v-if="$store.state.app.navbar">
      <Navbar />
    </template>
    <main>
      <router-view />
    </main>
    <AppFooter />
    <ExclusiveControlModal />
    <ErrorModal />
  </div>
</template>

<script>
import Menu from '@/utils/Menu'
import { renderError } from '@/utils/error'
import ExclusiveControlModal from '@/components/ExclusiveControlModal.vue'
import ErrorModal from '@/components/ErrorModal.vue'

const CEREMONY_BODY_CLASSES = ['ceremony-pre', 'ceremony-day', 'ceremony-post'] // applyBodyLayoutClassesで除去対象

export default {
  metaInfo() {
    const info = {
      titleTemplate: title => {
        if (!title) {
          return this.theme.serviceName
        }

        const decoded = this.$domUtils.decode(title)
        return `${this.theme.serviceName} | ${decoded}`
      },
      link: [
        { rel: 'icon', href: this.theme.favicon }
      ],
      // 各画面のbodyAttrs適用後に呼ばれる。店舗ID・挙式状態を付け直す
      changed: () => {
        this.applyBodyLayoutClasses()
      }
    }

    if (process.env.NODE_ENV !== 'development') {
      info.link.push({ rel: 'stylesheet', href: this.theme.css })
    }

    return info
  },
  components: {
    ExclusiveControlModal,
    ErrorModal,
  },
  computed: {
    appClassNames() {
      return {
        'is-menu-open': this.$store.getters['navbar/isSideMenuOpen'],
        'is-loading': this.$store.state.app.loading
      }
    },
    theme() {
      return this.$store.state.theme
    },
    businessId() {
      return this.$store.getters['auth/businessId']
    },
    isAuthenticated() {
      return this.$store.getters['auth/check']
    },
    officeId() {
      return this.$store.state.app.officeId
    },
    ceremonyStatus() {
      return this.$store.state.app.ceremonyStatus
    },
    pageName() {
      const currentPath = this.$route.path.endsWith('/')
        ? this.$route.path.slice(0, -1)
        : this.$route.path

      for (let menu of this.$store.state.app.menus) {
        menu = new Menu(menu)

        if (menu.url.path === currentPath) {
          return menu.name
        }
      }
      return ''
    }
  },
  async created() {
    try {
      // ログイン済みであればスタッフを取得する
      if (this.$store.getters['auth/check']) {
        await this.$store.dispatch('app/fetchStaff', this.businessId)
      }
    } catch(err) {
      renderError(err)
    }
  },
  errorCaptured(err) {
    renderError(err)
  },
  watch: {
    $route: {
      handler() {
        this.$store.commit('app/SET_PAGE_NAME', this.pageName)
      },
      immediate: true
    },
    officeId() {
      this.applyBodyLayoutClasses()
    },
    ceremonyStatus() {
      this.applyBodyLayoutClasses()
    },
    isAuthenticated() {
      this.applyBodyLayoutClasses()
    }
  },
  methods: {
    /**
     * vue-metaのbodyAttrsは画面classで上書きされるため、
     * meta更新後（changed）およびstore更新時にbodyへ付け直す
     */
    applyBodyLayoutClasses() {
      // 共通レイアウト用classのみ除去（画面固有classはvue-metaが管理）
      document.body.classList.forEach((cls) => {
        if (/^office-\d+$/.test(cls) || CEREMONY_BODY_CLASSES.includes(cls)) {
          document.body.classList.remove(cls)
        }
      })

      // ログアウト後・ログイン前は除去のみ（前案件のclassを付け直さない）
      if (!this.isAuthenticated) {
        return
      }

      // fetchMenu完了前（officeId未設定）は付与しない
      if (this.officeId != null) {
        document.body.classList.add(`office-${this.officeId}`)
      }
      if (this.ceremonyStatus) {
        document.body.classList.add(this.ceremonyStatus)
      }
    }
  }
}
</script>

<style lang="scss">
@import '@/styles/app.scss';
</style>
